'use client';

import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';

const containers = [
  {
    id: 'standard-container',
    label: 'Standard Container',
    aka: '20\' & 40\' Dry Container',
    icon: (
      <svg viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-12" aria-label="Standard container icon">
        <rect x="4" y="10" width="72" height="40" rx="3" stroke="#60A5FA" strokeWidth="3" fill="none"/>
        <line x1="4" y1="30" x2="76" y2="30" stroke="#60A5FA" strokeWidth="1.5" strokeDasharray="5 3"/>
        <line x1="28" y1="10" x2="28" y2="50" stroke="#60A5FA" strokeWidth="1.5"/>
        <line x1="52" y1="10" x2="52" y2="50" stroke="#60A5FA" strokeWidth="1.5"/>
        <rect x="34" y="22" width="12" height="16" rx="2" stroke="#93C5FD" strokeWidth="2" fill="none"/>
      </svg>
    ),
    description: 'The Standard Dry Container is the most widely used container type in international shipping. Fully enclosed with rigid walls, floor, and roof, it protects cargo from weather and external elements.',
    specs: [
      { label: 'Types', value: "20' (TEU) and 40' (FEU)" },
      { label: '20\' Internal Dimensions', value: '5.9m L × 2.35m W × 2.39m H' },
      { label: '40\' Internal Dimensions', value: '12.03m L × 2.35m W × 2.39m H' },
      { label: '20\' Max Payload', value: '≈ 28,200 kg' },
      { label: '40\' Max Payload', value: '≈ 26,700 kg' },
      { label: '20\' Volume', value: '≈ 33 m³' },
      { label: '40\' Volume', value: '≈ 67 m³' },
    ],
    suitable: ['General dry goods and packaged cargo', 'Electronics and machinery', 'Furniture and household goods', 'Textiles and apparel', 'Non-perishable food products'],
    color: '#60A5FA',
  },
  {
    id: 'open-top-container',
    label: 'Open Top Container',
    aka: 'OT Container',
    icon: (
      <svg viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-12" aria-label="Open top container icon">
        <rect x="4" y="20" width="72" height="30" rx="3" stroke="#34D399" strokeWidth="3" fill="none"/>
        <line x1="4" y1="35" x2="76" y2="35" stroke="#34D399" strokeWidth="1.5" strokeDasharray="5 3"/>
        <line x1="28" y1="20" x2="28" y2="50" stroke="#34D399" strokeWidth="1.5"/>
        <line x1="52" y1="20" x2="52" y2="50" stroke="#34D399" strokeWidth="1.5"/>
        <line x1="4" y1="20" x2="76" y2="20" stroke="#34D399" strokeWidth="3" strokeDasharray="8 4"/>
        <path d="M20 8 L20 20 M40 5 L40 20 M60 8 L60 20" stroke="#6EE7B7" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    description: 'Open Top Containers have a removable tarpaulin roof instead of a solid top, allowing cargo to be loaded from above using cranes. Ideal for oversized or tall cargo that cannot fit through standard container doors.',
    specs: [
      { label: 'Types', value: "20\' and 40\' Open Top" },
      { label: '20\' Internal Dimensions', value: '5.9m L × 2.35m W × 2.35m H' },
      { label: '40\' Internal Dimensions', value: '12.03m L × 2.35m W × 2.35m H' },
      { label: '20\' Max Payload', value: '≈ 28,100 kg' },
      { label: '40\' Max Payload', value: '≈ 26,500 kg' },
      { label: 'Roof', value: 'Removable tarpaulin cover' },
    ],
    suitable: ['Machinery and industrial equipment', 'Steel coils and metal structures', 'Timber and lumber', 'Large construction materials', 'Oversized cargo loaded by crane'],
    color: '#34D399',
  },
  {
    id: 'refrigerated-container',
    label: 'Refrigerated Container',
    aka: 'Reefer Container',
    icon: (
      <svg viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-12" aria-label="Refrigerated container icon">
        <rect x="4" y="10" width="72" height="40" rx="3" stroke="#818CF8" strokeWidth="3" fill="none"/>
        <rect x="10" y="16" width="18" height="28" rx="2" stroke="#818CF8" strokeWidth="2" fill="none"/>
        <path d="M40 18 L40 42 M34 24 L46 24 M34 36 L46 36" stroke="#A5B4FC" strokeWidth="2" strokeLinecap="round"/>
        <path d="M55 20 Q62 30 55 40" stroke="#A5B4FC" strokeWidth="2" fill="none" strokeLinecap="round"/>
        <path d="M62 16 Q72 30 62 44" stroke="#A5B4FC" strokeWidth="2" fill="none" strokeLinecap="round"/>
      </svg>
    ),
    description: 'Refrigerated Containers (Reefers) are temperature-controlled units equipped with a built-in refrigeration system. They maintain precise temperatures from -30°C to +30°C, ensuring perishable goods arrive in perfect condition.',
    specs: [
      { label: 'Types', value: "20\' and 40\' Reefer" },
      { label: 'Temperature Range', value: '-30°C to +30°C' },
      { label: '20\' Internal Dimensions', value: '5.44m L × 2.27m W × 2.25m H' },
      { label: '40\' Internal Dimensions', value: '11.56m L × 2.27m W × 2.25m H' },
      { label: '20\' Volume', value: '≈ 28 m³' },
      { label: '40\' Volume', value: '≈ 59 m³' },
      { label: 'Power', value: 'Requires external power supply' },
    ],
    suitable: ['Fresh fruits and vegetables', 'Meat, fish, and seafood', 'Dairy products', 'Pharmaceuticals and medicines', 'Flowers and plants', 'Chemicals requiring temperature control'],
    color: '#818CF8',
  },
  {
    id: 'flat-rack-container',
    label: 'Flat Rack Container',
    aka: 'FR Container',
    icon: (
      <svg viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-12" aria-label="Flat rack container icon">
        <rect x="4" y="38" width="72" height="8" rx="2" stroke="#F59E0B" strokeWidth="3" fill="none"/>
        <rect x="4" y="10" width="10" height="36" rx="2" stroke="#F59E0B" strokeWidth="3" fill="none"/>
        <rect x="66" y="10" width="10" height="36" rx="2" stroke="#F59E0B" strokeWidth="3" fill="none"/>
        <line x1="14" y1="20" x2="66" y2="20" stroke="#FCD34D" strokeWidth="1.5" strokeDasharray="5 3"/>
        <line x1="14" y1="30" x2="66" y2="30" stroke="#FCD34D" strokeWidth="1.5" strokeDasharray="5 3"/>
      </svg>
    ),
    description: 'Flat Rack Containers have collapsible end walls and no side walls or roof, providing a flat platform for loading oversized, heavy, or irregularly shaped cargo. The end walls can be folded down for easy loading.',
    specs: [
      { label: 'Types', value: "20\' and 40\' Flat Rack" },
      { label: '20\' Platform Dimensions', value: '5.62m L × 2.23m W' },
      { label: '40\' Platform Dimensions', value: '12.13m L × 2.23m W' },
      { label: '20\' Max Payload', value: '≈ 28,000 kg' },
      { label: '40\' Max Payload', value: '≈ 40,000 kg' },
      { label: 'End Walls', value: 'Collapsible / foldable' },
    ],
    suitable: ['Heavy machinery and vehicles', 'Boats and yachts', 'Steel pipes and beams', 'Construction equipment', 'Oversized industrial components', 'Cargo wider or taller than standard containers'],
    color: '#F59E0B',
  },
  {
    id: 'platform-container',
    label: 'Platform Container',
    aka: 'Bolster / Flatrack without ends',
    icon: (
      <svg viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-12" aria-label="Platform container icon">
        <rect x="4" y="36" width="72" height="10" rx="2" stroke="#F87171" strokeWidth="3" fill="none"/>
        <line x1="4" y1="41" x2="76" y2="41" stroke="#FCA5A5" strokeWidth="1.5"/>
        <line x1="16" y1="36" x2="16" y2="46" stroke="#F87171" strokeWidth="2"/>
        <line x1="32" y1="36" x2="32" y2="46" stroke="#F87171" strokeWidth="2"/>
        <line x1="48" y1="36" x2="48" y2="46" stroke="#F87171" strokeWidth="2"/>
        <line x1="64" y1="36" x2="64" y2="46" stroke="#F87171" strokeWidth="2"/>
        <path d="M20 20 L60 20 L60 36 L20 36 Z" stroke="#FCA5A5" strokeWidth="2" fill="none" strokeDasharray="6 3"/>
      </svg>
    ),
    description: 'Platform Containers are essentially a flat base with no walls or roof — just a solid steel floor platform with corner castings. They offer maximum flexibility for loading extremely large, heavy, or awkwardly shaped cargo from any direction.',
    specs: [
      { label: 'Types', value: "20\' and 40\' Platform" },
      { label: '20\' Platform Dimensions', value: '5.62m L × 2.23m W' },
      { label: '40\' Platform Dimensions', value: '12.13m L × 2.23m W' },
      { label: '20\' Max Payload', value: '≈ 30,480 kg' },
      { label: '40\' Max Payload', value: '≈ 45,000 kg' },
      { label: 'Structure', value: 'Floor only — no walls or roof' },
    ],
    suitable: ['Extremely heavy industrial machinery', 'Large transformers and generators', 'Pre-fabricated structures', 'Oversized project cargo', 'Cargo requiring multi-directional loading'],
    color: '#F87171',
  },
];

function UsefulInfoContent() {
  const searchParams = useSearchParams();
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    const target = searchParams.get('type');
    if (target) {
      const el = sectionRefs.current[target];
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      }
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6F9' }}>
      <Header />
      <main className="flex-1 pt-32 pb-20">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
          <div className="w-10 h-1 mb-5 rounded" style={{ background: '#1B6B8A' }} />
          <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight" style={{ color: '#0F2340', fontFamily: 'var(--font-display, sans-serif)' }}>
            Useful Information
          </h1>
          <p className="text-lg max-w-2xl leading-relaxed" style={{ color: '#3A5070' }}>
            Everything you need to know about container types used in international freight shipping — dimensions, capacities, and ideal cargo types.
          </p>

          {/* Quick nav */}
          <div className="flex flex-wrap gap-3 mt-8">
            {containers.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200"
                style={{ border: `1px solid ${c.color}50`, background: `${c.color}15`, color: c.color }}
              >
                {c.label}
              </a>
            ))}
          </div>
        </section>

        {/* Container sections */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 space-y-16 pb-8">
          {containers.map((container) => (
            <div
              key={container.id}
              id={container.id}
              ref={(el) => { sectionRefs.current[container.id] = el; }}
              className="rounded-2xl p-8 lg:p-10 scroll-mt-32"
              style={{ background: '#FFFFFF', border: `1px solid ${container.color}25`, boxShadow: '0 2px 12px rgba(15,35,64,0.07)' }}
            >
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                {/* Left: icon + title */}
                <div className="lg:w-64 flex-shrink-0">
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mb-4"
                    style={{ background: `${container.color}15`, border: `1px solid ${container.color}30` }}
                  >
                    {container.icon}
                  </div>
                  <h2 className="text-2xl font-bold mb-1" style={{ color: '#0F2340' }}>{container.label}</h2>
                  <p className="text-sm font-medium" style={{ color: container.color }}>{container.aka}</p>
                </div>

                {/* Right: details */}
                <div className="flex-1 space-y-6">
                  <p className="text-base leading-relaxed" style={{ color: '#3A5070' }}>{container.description}</p>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Specs */}
                    <div>
                      <h3 className="text-xs uppercase tracking-widest mb-3 font-semibold" style={{ color: '#6A85A0' }}>Specifications</h3>
                      <div className="space-y-2">
                        {container.specs.map((spec) => (
                          <div key={spec.label} className="flex justify-between gap-4 py-1.5" style={{ borderBottom: '1px solid rgba(15,35,64,0.08)' }}>
                            <span className="text-sm" style={{ color: '#6A85A0' }}>{spec.label}</span>
                            <span className="text-sm font-medium text-right" style={{ color: '#0F2340' }}>{spec.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Suitable for */}
                    <div>
                      <h3 className="text-xs uppercase tracking-widest mb-3 font-semibold" style={{ color: '#6A85A0' }}>Suitable For</h3>
                      <ul className="space-y-2">
                        {container.suitable.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm" style={{ color: '#3A5070' }}>
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: container.color }} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* CTA */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 pt-8">
          <div className="rounded-2xl p-8 text-center" style={{ background: '#0F2340', border: '1px solid rgba(27,107,138,0.3)' }}>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#FFFFFF' }}>Need help choosing the right container?</h3>
            <p className="text-sm mb-6" style={{ color: 'rgba(200,216,240,0.85)' }}>Our logistics experts are ready to advise you on the best solution for your cargo.</p>
            <Link
              href="/transport-quote"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200"
              style={{ background: '#1A5FAB', color: '#FFFFFF' }}
            >
              Request a Quote
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default function UsefulInfoPage() {
  return (
    <Suspense fallback={<div className="min-h-screen" style={{ background: '#F4F6F9' }} />}>
      <UsefulInfoContent />
    </Suspense>
  );
}
